import { inject, Injectable } from '@angular/core';
import { InMemoryCacheService } from 'app/shared/cache/in-memory-cache.service';
import { Point } from 'app/shared/ll/models/geometry';
import { combineLatest, of } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { GoogleLoader } from './google-loader.service';
import { GOOGLE_API_KEY } from './google.tokens';


export type Directions = { location: google.maps.LatLng; heading: number };


@Injectable()
export class GoogleStreetViewImageService {

    private googleApiKey = inject(GOOGLE_API_KEY);

    private googleLoader = inject(GoogleLoader);

    private imc = inject(InMemoryCacheService<Directions>);

    private requestDirections(center: Point, address: string) {
        return new Promise<Directions>((resolve, reject) => {
            combineLatest([
                this.googleLoader.loadRoute(),
                this.googleLoader.loadGeometry()
            ]).subscribe({
                next: ([Route]) => {
                    const routeConfig = {
                        origin: address,
                        destination: address,
                        travelMode: google.maps.TravelMode.DRIVING,
                        fields: ['legs'],
                    };
                    try {
                        Route.computeRoutes(routeConfig).then(({ routes }) => {
                            const startLocation = routes?.[0]?.legs?.[0]?.startLocation;
                            if (!startLocation) {
                                return reject(new Error('Routes service returned no start location'));
                            }
                            const location = new google.maps.LatLng(startLocation.lat, startLocation.lng);
                            const [lng, lat] = center.coordinates;
                            const endLatLng = new google.maps.LatLng(lat, lng);

                            const heading = google.maps.geometry.spherical.computeHeading(location, endLatLng);
                            return resolve({ location, heading });
                        }).catch(reject);
                    }
                    catch (e) {
                        reject(e);
                    }
                },
                error: reject
            });
        });
    }

    getDirections(center: Point, address: string) {
        return this.imc.get(address)
            .pipe(
                take(1),
                mergeMap(directions => {
                    if (directions) {
                        return of(directions);
                    }
                    return this.requestDirections(center, address)
                        .then(d => {
                            this.imc.set(address, d);
                            return d;
                        })
                        .catch(() => {
                            this.imc.set(address, null);
                            return null;
                        }
                    );
                }),
            );
    }

    private makeImageUrl(directions: Directions, width: number, height: number) {
        const size = `${width}x${height}`;
        const location = `${directions.location.lat()},${directions.location.lng()}`;
        const params = `size=${size}&location=${location}&heading=${directions.heading}&pitch=2&key=${this.googleApiKey}&source=outdoor`;
        return `https://maps.googleapis.com/maps/api/streetview?${params}`;
    }

    getImageUrl(center: Point, address: string, width: number, height: number) {
        return this.getDirections(center, address)
            .pipe(
                map(directions => directions ? this.makeImageUrl(directions, width, height) : ''),
            );
    }
}
